// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AttachableCharge } from '@/billing/api/get-attachable-charges';
import { AddLineItemForm } from '@/billing/components/add-line-item-form';
import { NewChargeForm } from '@/billing/components/new-charge-form';
import { useAttachableCharges } from '@/billing/hooks/use-attachable-charges';
import { AddLineItemFormData } from '@/billing/schemas/add-line-item-form-schema';
import { NewChargeFormData } from '@/billing/schemas/new-charge-form-schema';
import { FilterToggleGroup } from '@/components/filter-toggle-group';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { SparseProject } from '@/sparse/api/types';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type DialogMode = 'existing' | 'new';

const TOGGLE_OPTIONS = [
  { value: 'new', label: 'For a new charge' },
  { value: 'existing', label: 'From an existing charge' },
] as const;

const EXISTING_FORM_ID = 'add-line-item-form';
const NEW_FORM_ID = 'new-charge-form';

interface AddLineItemDialogProps {
  invoiceKey: string;
  currencyCode: IsoCurrencyCode;
  project: SparseProject | null;
  isOpen: boolean;
  onClose: () => void;
  queuedChargeKeys: ReadonlySet<string>;
  onAdd: (charge: AttachableCharge, billingEligibleDate: DateTime) => void;
  onAddAdhoc: (data: NewChargeFormData) => void;
}

export function AddLineItemDialog(props: AddLineItemDialogProps) {
  const [mode, setMode] = useState<DialogMode>('new');
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // The dialog stays mounted under its parent, so `mode` survives close →
  // reopen by default. Reset to the 'new' default on the false → true
  // transition of isOpen, mirroring NewChargeForm's reset-guard pattern.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (props.isOpen && !wasOpenRef.current) {
      setMode('new');
      setIsFormDirty(false);
      setShowUnsavedDialog(false);
    }
    wasOpenRef.current = props.isOpen;
  }, [props.isOpen]);

  // Switching modes unmounts one form and mounts the other. The mounted
  // form will report its actual isDirty via onDirtyChange; clear the
  // stale flag here so the parent doesn't trip the unsaved-changes
  // blocker on a fresh form.
  useEffect(() => {
    setIsFormDirty(false);
  }, [mode]);

  const attachableQuery = useAttachableCharges(props.invoiceKey, props.isOpen);
  const charges = useMemo<AttachableCharge[]>(() => {
    const all = attachableQuery.data ?? [];
    if (props.queuedChargeKeys.size === 0) return all;
    return all.filter((c) => !props.queuedChargeKeys.has(c.chargeKey));
  }, [attachableQuery.data, props.queuedChargeKeys]);

  const handleExistingSubmit = useCallback(
    (data: AddLineItemFormData) => {
      if (data.charge === null) return;
      props.onAdd(data.charge, data.billingEligibleDate);
      props.onClose();
    },
    [props]
  );

  const handleNewSubmit = useCallback(
    (data: NewChargeFormData) => {
      props.onAddAdhoc(data);
      props.onClose();
    },
    [props]
  );

  const attemptClose = useCallback(() => {
    if (isFormDirty) {
      setShowUnsavedDialog(true);
    } else {
      props.onClose();
    }
  }, [isFormDirty, props]);

  const forceClose = useCallback(() => {
    setShowUnsavedDialog(false);
    setIsFormDirty(false);
    props.onClose();
  }, [props]);

  const existingDisabled = charges.length === 0 || attachableQuery.isError;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={attemptClose}
      scroll="paper"
    >
      <DialogTitle>Add line item</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FilterToggleGroup
            value={mode}
            options={[...TOGGLE_OPTIONS]}
            onChange={setMode}
            ariaLabel="Add line item mode"
            size="small"
            fullWidth
          />
          <Typography variant="body2" color="text.secondary">
            {mode === 'new'
              ? 'Create a line item as a net new charge for '
              : 'Match an existing charge from '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {props.project?.name ?? 'this project'}
            </Box>
          </Typography>

          {mode === 'existing' && (
            <>
              {attachableQuery.isPending ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : attachableQuery.isError ? (
                <Alert severity="error">
                  Could not load attachable charges. Close the dialog and try
                  again.
                </Alert>
              ) : charges.length === 0 ? (
                <Alert severity="info">
                  <AlertTitle>
                    This project currently has no planned or billable charges
                  </AlertTitle>
                  To invoice against a pre-existing charge, you can:
                  <ul>
                    <li>
                      Confirm billing plans are correct for each line item
                    </li>
                    <li>Create a new billing plan on a line item</li>
                    <li>Add a new line item and corresponding billing plan</li>
                  </ul>
                </Alert>
              ) : (
                <AddLineItemForm
                  formId={EXISTING_FORM_ID}
                  isDisabled={false}
                  isOpen={props.isOpen}
                  charges={charges}
                  onSubmit={handleExistingSubmit}
                  onDirtyChange={setIsFormDirty}
                />
              )}
            </>
          )}

          {mode === 'new' &&
            (props.project === null ? (
              <Alert severity="info">
                This invoice has no project. Assign one before creating an
                ad-hoc charge so the new line item has a project to bind to.
              </Alert>
            ) : (
              <NewChargeForm
                formId={NEW_FORM_ID}
                currencyCode={props.currencyCode}
                projectStart={props.project.startOn}
                projectEnd={props.project.endOn}
                priorityCustomerId={props.project.customer?.id ?? null}
                isOpen={props.isOpen}
                isDisabled={false}
                onSubmit={handleNewSubmit}
                onDirtyChange={setIsFormDirty}
              />
            ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={attemptClose}>Cancel</Button>
        {mode === 'existing' ? (
          <Button
            type="submit"
            form={EXISTING_FORM_ID}
            variant="contained"
            color="primary"
            disabled={existingDisabled}
          >
            Add
          </Button>
        ) : (
          <Button
            type="submit"
            form={NEW_FORM_ID}
            variant="contained"
            color="primary"
            disabled={props.project === null}
          >
            Add
          </Button>
        )}
      </DialogActions>
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onCancel={() => setShowUnsavedDialog(false)}
        onProceed={forceClose}
      />
    </Dialog>
  );
}
